import { graphql, useStaticQuery } from 'gatsby'

/*
How to add new image:
1. Place an image in the src/assets/images folder
2. Add extend gatsby query with gatsbyImageData:
  - docs for all parameters used in gatsbyImageData - https://www.gatsbyjs.com/docs/reference/built-in-components/gatsby-plugin-image
  - property name should match filename but with camelCase notation (and without digits at the beggining)
  - use the best quality image, because it leaves the most space for optimization
  - set image quality between 60-80, these values works the best
How gatsby-plugin-image works:
- reduces images sizes
- cuts them to passed dimensions
- creates multiple image versions for srcSet (1x, 1,5x, 2x)
- creates scrSet with all the fastest supported file formats
- gatsby-plugin-image's components will fallback to png/jpg if browser is not supporting webp format
*/
export const useImages = () => {
  // /**
  // * Please note this image fragment requires a wrapper
  // */

  return useStaticQuery(graphql`
    fragment categoriesImage on File {
      childImageSharp {
        gatsbyImageData(quality: 80, placeholder: NONE)
      }
    }

    fragment promoBoxes on File {
      childImageSharp {
        gatsbyImageData(quality: 80, placeholder: NONE)
      }
    }

    fragment widgetPlacement on File {
      childImageSharp {
        gatsbyImageData(width: 456, height: 262, quality: 65, placeholder: NONE)
      }
    }

    query {
      genericApps: file(
        relativePath: { eq: "images/app-hero/generic-apps-2x.png" }
      ) {
        childImageSharp {
          gatsbyImageData(
            width: 320
            quality: 60
            layout: FIXED
            placeholder: DOMINANT_COLOR
          )
        }
      }
      cartIcon: file(relativePath: { eq: "icons/icon-cart.png" }) {
        childImageSharp {
          gatsbyImageData(
            width: 48
            height: 48
            layout: FIXED
            placeholder: NONE
          )
        }
      }
      fallbackThumbnail: file(
        relativePath: { eq: "images/app-logos/fallback-thumbnail.png" }
      ) {
        childImageSharp {
          gatsbyImageData(width: 88, height: 88, placeholder: NONE)
        }
      }
      analytics: file(
        relativePath: { eq: "images/categories-covers/analytics-category.png" }
      ) {
        ...categoriesImage
      }
      cms: file(
        relativePath: { eq: "images/categories-covers/cms-category.png" }
      ) {
        ...categoriesImage
      }
      crm: file(
        relativePath: { eq: "images/categories-covers/crm-category.png" }
      ) {
        ...categoriesImage
      }
      default: file(
        relativePath: { eq: "images/categories-covers/default@4x.png" }
      ) {
        ...categoriesImage
      }
      ecommerce: file(
        relativePath: { eq: "images/categories-covers/ecommerce-category.png" }
      ) {
        ...categoriesImage
      }
      helpdesk: file(
        relativePath: { eq: "images/categories-covers/helpdesk-category.png" }
      ) {
        ...categoriesImage
      }
      email: file(
        relativePath: { eq: "images/categories-covers/email-category.png" }
      ) {
        ...categoriesImage
      }
      cartEmptyState: file(
        relativePath: { eq: "images/cart/empty-state.png" }
      ) {
        childImageSharp {
          gatsbyImageData(
            width: 500
            quality: 60
            layout: CONSTRAINED
            placeholder: NONE
          )
        }
      }
      enhanceEcommerceBanner: file(
        relativePath: { eq: "images/enhance-ecommerce/bannerHero.png" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 60, placeholder: NONE, layout: FULL_WIDTH)
        }
      }
      omnichannelAvatar: file(
        relativePath: { eq: "images/omnichannel/avatar.png" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 60, placeholder: NONE)
        }
      }
      omnichannelCampaignHero: file(
        relativePath: { eq: "images/omnichannel/campaignHero@3x.png" }
      ) {
        childImageSharp {
          gatsbyImageData(
            quality: 60
            transformOptions: { fit: CONTAIN }
            layout: FULL_WIDTH
            placeholder: NONE
          )
        }
      }
      helpdeskCampaignHero: file(
        relativePath: { eq: "images/helpdesk-dev-platform/campaignHero@3x.png" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 60, placeholder: NONE, layout: FULL_WIDTH)
        }
      }
      helpdeskAppsCampaignHero: file(
        relativePath: { eq: "images/helpdesk-campaign/hero@2x.png" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 60, placeholder: NONE, layout: FULL_WIDTH)
        }
      }
      devPlatformLogo: file(
        relativePath: {
          eq: "images/helpdesk-dev-platform/dev-platform-logo.png"
        }
      ) {
        childImageSharp {
          gatsbyImageData(placeholder: NONE)
        }
      }
      platformCtaHero: file(
        relativePath: { eq: "images/platform-cta-hero.png" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 90, placeholder: NONE)
        }
      }
      partnerProgramCards: file(
        relativePath: { eq: "images/partner-program-cards.png" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 90, placeholder: NONE)
        }
      }
      partnerMatchmaking: file(
        relativePath: { eq: "images/partner-matchmaking.png" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 90, placeholder: NONE)
        }
      }
      selfSetupBanner: file(
        relativePath: { eq: "images/self-setup/bannerHero.png" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 90, placeholder: NONE)
        }
      }
      launchFinger: file(
        relativePath: { eq: "images/homepage/launch-finger.png" }
      ) {
        childImageSharp {
          gatsbyImageData(
            width: 260
            quality: 60
            layout: CONSTRAINED
            placeholder: NONE
          )
        }
      }
      useLiveChat: file(
        relativePath: { eq: "images/homepage/use-livechat.png" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 90, layout: CONSTRAINED, placeholder: NONE)
        }
      }
      widgetFullscreenPlacement: file(
        relativePath: {
          eq: "images/widget-previews/widget-card-fullscreen-app.png"
        }
      ) {
        ...widgetPlacement
      }
      widgetSettingsPlacement: file(
        relativePath: { eq: "images/widget-previews/widget-card-settings.png" }
      ) {
        ...widgetPlacement
      }
      widgetCustomerDetailsPlacement: file(
        relativePath: {
          eq: "images/widget-previews/widget-card-customer-details.png"
        }
      ) {
        ...widgetPlacement
      }
      widgetMessageBoxPlacement: file(
        relativePath: {
          eq: "images/widget-previews/widget-card-message-box.png"
        }
      ) {
        ...widgetPlacement
      }
      widgetReportsPlacement: file(
        relativePath: { eq: "images/widget-previews/widget-card-reports.png" }
      ) {
        ...widgetPlacement
      }
      expertsModal: file(
        relativePath: { eq: "images/experts/experts-modal.png" }
      ) {
        childImageSharp {
          gatsbyImageData(quality: 80, placeholder: NONE)
        }
      }
    }
  `)
}
